import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

import Dashboard from 'components/Dashboard';
import GroupedLoadingContext from 'contexts/GroupedLoadingContext';
import useLegacySavedFilter from 'hooks/useLegacySavedFilter';
import PRINT_LOADING_SELECTOR from 'screens/PrintReportShow/printLoadingSelector';

import { useDashboard } from '../DashboardShow/DashboardShow';

// The dashboard's own wrapper is a viewport-height scroll container, so we don't
// flatten heights (that collapses the layout). The renderer grows the viewport
// to the content height so it all fits. Here we just strip the body margin,
// ensure a solid background, and hide the variable-filter controls bar (it
// carries the EXCLUDE_HEIGHT class) so the PDF is the gadget grid only.
const PrintGlobalStyle = createGlobalStyle`
  body { margin: 0; background: var(--color-bg-primary); }
  #print-dashboard-root [class*="exclude-height-"] { display: none !important; }
`;

// Fixed render width for a consistent layout regardless of the runner's viewport.
const PrintRoot = styled.div`
  width: 1280px;
  background: var(--color-bg-primary);
`;

// Poll interval for the "fully rendered" check.
const POLL_MS = 700;
// Absolute safety net so we always signal rather than hanging to the renderer's
// wait timeout.
const MAX_WAIT_MS = 90000;

const PrintDashboardShow = () => {
  // This screen isn't mounted under a :dashboardId <Route>, so derive the id
  // from the path rather than useParams (which would be empty here).
  const { pathname, search } = useLocation();
  const dashboardId = pathname.split('/print/dashboards/')[1]?.split(/[?/]/)[0];
  const savedFilterId =
    new URLSearchParams(search).get('savedFilterId') ?? undefined;

  const { dashboard, isLoading: dashboardLoading } = useDashboard(dashboardId);
  const { savedFilter, isLoading: savedFilterLoading } =
    useLegacySavedFilter(savedFilterId);
  const { isLoading: dataLoading } = useContext(GroupedLoadingContext);
  const [ready, setReady] = useState(false);

  // Replay the scheduled variable-filter snapshot by overriding the dashboard's
  // persisted variableDrillDowns. The dashboard container initialises its live
  // variable filters from this value, so no other wiring is needed.
  const dashboardWithSnapshot = useMemo(() => {
    if (!dashboard) return undefined;
    if (savedFilter?.variableDrillDowns) {
      return {
        ...dashboard,
        variableDrillDowns: savedFilter.variableDrillDowns,
      };
    }
    return dashboard;
  }, [dashboard, savedFilter]);

  // Ready once the dashboard + snapshot have loaded AND there are no loading
  // spinners, gadget skeletons (see PRINT_LOADING_SELECTOR) or pending grid
  // refits left in the DOM. Require it stable across a few polls, then wait for
  // fonts + two paints. A hard cap guarantees we always signal rather than
  // hanging.
  useEffect(() => {
    if (
      ready ||
      dashboardLoading ||
      savedFilterLoading ||
      !dashboardWithSnapshot
    ) {
      return;
    }
    let cancelled = false;
    let stable = 0;
    const finish = async () => {
      if (document.fonts?.ready) {
        await document.fonts.ready.catch(() => undefined);
      }
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      if (!cancelled) setReady(true);
    };
    const poll = setInterval(() => {
      const stillLoading =
        dataLoading ||
        document.querySelectorAll(PRINT_LOADING_SELECTOR).length > 0 ||
        // Wait for grid cards to finish refitting to their content (see
        // ReportCanvas buildHeadlessRefitLayout) before capturing.
        document.querySelectorAll('[data-headless-refit-pending="true"]')
          .length > 0;
      if (stillLoading) {
        stable = 0;
        return;
      }
      stable += 1;
      if (stable >= 3) {
        clearInterval(poll);
        finish();
      }
    }, POLL_MS);
    const cap = setTimeout(() => {
      clearInterval(poll);
      if (!cancelled) setReady(true);
    }, MAX_WAIT_MS);
    return () => {
      cancelled = true;
      clearInterval(poll);
      clearTimeout(cap);
    };
  }, [
    ready,
    dashboardLoading,
    savedFilterLoading,
    dashboardWithSnapshot,
    dataLoading,
  ]);

  return (
    <PrintRoot
      id="print-dashboard-root"
      data-print-ready={ready ? 'true' : undefined}
    >
      <PrintGlobalStyle />
      {dashboardWithSnapshot && (
        <Dashboard
          dashboard={dashboardWithSnapshot}
          key={dashboardWithSnapshot.id}
          forceHeadlessRender
        />
      )}
    </PrintRoot>
  );
};

export default PrintDashboardShow;
